/**
 * A Globalixba ágyazott keret (#273, `.md/GLOBALIX-beagyazott-menuk-273.md`).
 *
 * A Globalix iframe-ben nyitja a `/embed/sso/globalix?token=…` címet; a token beváltása után a keret a
 * MEGLÉVŐ oldalakat mutatja (Előjegyzési naptáram, Páciensek, Elszámolásaim), oldalsáv és fejléc
 * nélkül — így a karton, a foglalás ablaka és a belső linkek ugyanúgy működnek, mint az XMED-ben.
 *
 * Három dolog, ami a keretben más:
 *
 * - **Saját munkamenet.** A szerver a keret kéréseire más nevű munkamenet- és CSRF-sütit ad (az
 *   `X-XMED-Embed` fejlécből tudja), mert élesen a Globalix és a keret egy webhely: közös sütin a keret
 *   belépése a másik fülön futó XMED-et is átírná.
 * - **Saját tároló.** Ugyanezért a keret nem ír a `localStorage`-ba (az élesen közös a normál füllel):
 *   amit a felület máskor ott tart, a keretben a fülhöz kötött `sessionStorage`-ba kerül.
 * - **Nincs belépő oldal.** Lejárt munkamenetnél a keret üzenetet mutat, és szól a Globalixnak
 *   (`postMessage`), hogy új tokennel nyissa újra.
 */
const STATE_KEY = 'xmed.embed';

/**
 * A Globalix menüpont-kódjai: hová nyílik a keret, és mely útvonalak (és al-útvonalaik) érhetők el
 * belőle. A naptárból a páciens kartonja is nyílik, ezért az a naptár keretében is szabad.
 */
const PATIENTS = '/application/patients/patients';

export const EMBED_TARGETS = {
    'my-appointments': {name: 'application-appointments-my-appointments', prefixes: ['/application/appointments/my-appointments', PATIENTS]},
    'patients': {name: 'application-patients-patients', prefixes: [PATIENTS]},
    'my-settlements': {name: 'application-my-settlements', prefixes: ['/application/my-settlements']},
};

function readState() {
    try {
        return JSON.parse(window.sessionStorage.getItem(STATE_KEY) || 'null');
    } catch (e) {
        return null;
    }
}

function inFrame() {
    try {
        return window.self !== window.top;
    } catch (e) {
        return true; // idegen szülő: a hozzáférés tiltása maga a bizonyíték
    }
}

/**
 * A keretben fut-e a felület: iframe-ben, a beváltó oldalon vagy egy beváltott keret-munkamenetben
 * (újratöltés után is). A keret-állapot a fül `sessionStorage`-ában van, ami ugyanazon a fülön a teljes
 * lapos XMED-é is (pl. ha a Globalix ugyanazon a lapon irányít át): ezért kell a keretben futás is,
 * különben a normál XMED is keretként viselkedne.
 */
export function isEmbedded() {
    return inFrame() && (readState() !== null || window.location.pathname.startsWith('/embed/'));
}

/**
 * A keret tároló-kulcsa: előtaggal, hogy ugyanazon a fülön a teljes lapos XMED ne a keret
 * felhasználójának adatait olvassa vissza (és fordítva).
 */
export function storageKey(key) {
    return isEmbedded() ? 'xmed.embed:' + key : key;
}

/** A keret állapota a beváltás után: a menüpont, az intézmény, és ahová a lejárati üzenet mehet. */
export function rememberEmbed(state) {
    try {
        window.sessionStorage.setItem(STATE_KEY, JSON.stringify({
            target: state.target,
            tenant_id: state.tenant_id,
            origins: Array.isArray(state.origins) ? state.origins : (readState()?.origins || []),
            // a munkamenet a belépés után valóban megmaradt (egy hívás átment): csak ezután jelzünk lejáratot
            confirmed: !!state.confirmed,
        }));
    } catch (e) {
        // tároló nélkül a keret egy újratöltésig működik
    }
}

export function embedState() {
    return readState();
}

/** A keret menüpontjának útvonala (a beváltás után ide megy, és ide tér vissza egy tiltott navigációból). */
export function embedHome() {
    const target = EMBED_TARGETS[readState()?.target];

    return target ? {name: target.name} : null;
}

/**
 * Szabad-e a keretben ez az útvonal: a menüpont maga és ami belőle nyílik (karton, szerkesztés), a saját
 * profil, a beváltó és a lejárati oldal. Minden más a keret menüpontjára visz vissza.
 */
export function embedAllows(route) {
    if (route.path.startsWith('/embed/') || route.meta?.ownProfile) {
        return true;
    }

    const target = EMBED_TARGETS[readState()?.target];

    // a magyar cím (`/applikacio/...`) alias: újratöltéskor az alias rekordja illeszkedik, a kanonikus útvonal az `aliasOf`
    return !!target && route.matched.some(matched => {
        const path = matched.aliasOf?.path || matched.path || '';

        return target.prefixes.some(prefix => path.startsWith(prefix));
    });
}

/**
 * A böngésző-tároló, amelybe a felület a tartós állapotát írja: a keretben a fülhöz kötött
 * `sessionStorage`, máskor a `localStorage`.
 */
export function persistentStorage() {
    return isEmbedded() ? window.sessionStorage : window.localStorage;
}

/** A CSRF-süti neve: a keret a sajátját olvassa (a szerver `XSRF-TOKEN-EMBED` néven adja). */
export function xsrfCookieName() {
    return isEmbedded() ? 'XSRF-TOKEN-EMBED' : 'XSRF-TOKEN';
}

/**
 * Lejárt a keret munkamenete: üzenet a Globalixnak az okkal. A Globalix csak `expired`-nél nyitja újra
 * magától (egyszer); `idle`-nél (tétlenségi kiléptetés) és `replaced`-nél (egy másik Globalix-lap más
 * intézménnyel nyitotta meg) nem, különben a kiléptetés hatástalan lenne, a két lap pedig felváltva
 * írná felül egymást.
 */
export function notifyExpired(reason) {
    notifyParent('xmed:session-expired', {reason});
}

/**
 * Üzenet a beágyazó Globalixnak, CSAK egy engedett originre (soha `*`): a cél a keretet betöltő oldal
 * originje, ha az a szerver listáján van.
 */
export function notifyParent(type, extra = {}, origins = null) {
    if (window.parent === window) {
        return;
    }

    // belépés előtt (sikertelen beváltás) a szerver hibaválasza adja a listát
    const allowed = origins || readState()?.origins || [];
    const ancestor = window.location.ancestorOrigins?.[0];
    let referrer = null;

    try {
        referrer = document.referrer ? new URL(document.referrer).origin : null;
    } catch (e) {
        referrer = null;
    }

    const origin = [ancestor, referrer].find(candidate => candidate && allowed.includes(candidate));

    if (origin) {
        window.parent.postMessage({type, target: readState()?.target || null, ...extra}, origin);
    }
}
